import React from 'react';

/**
 * Enhanced Theme Configuration
 * Supports both legacy props and new theme presets
 */
export interface ThemeConfig {
    // Theme preset name (required)
    themeName?: string;

    // Title bar configuration
    showTitleBar?: boolean;

    /** Optional header logo replacing the default avatar in the title bar. */
    headerLogo?: {
        src: string; // image URL or imported asset (data URI in embed bundles)
        shape?: 'square' | 'wide'; // 'square' = 40×40 like the avatar; 'wide' = 28px tall, auto width, no tilt
        invert?: boolean; // render the image white via CSS (for dark logos on dark headers)
        alt?: string;
        /**
         * Optical vertical nudge in px (positive = down). The image box is
         * centred in the title bar, but a wordmark with descenders (e.g. the
         * "y" in YOUNA) has its baseline well above the box centre, so it
         * looks too high next to the title text. Set this to the distance
         * between the box-centred wordmark baseline and the title baseline.
         */
        offsetY?: number;
    };

    // Legacy color props (still supported)
    mainColor?: string;
    conversationBackgroundColor?: string;
    launcherBackgroundColor?: string;
    userTextColor?: string;
    userBackgroundColor?: string;
    assistTextColor?: string;
    assistBackgroundColor?: string;

    // New color system
    colors?: {
        primary?: string;
        background?: string;
        launcher?: string;
        userMessage?: string;
        userMessageText?: string;
        assistantMessage?: string;
        assistantMessageText?: string;
        headerBackground?: string;
        headerText?: string;
        sendButton?: string;
        sendButtonIcon?: string;
    };

    // Typography
    typography?: {
        fontFamily?: string;
        fontSize?: {
            base?: string;
            header?: string;
            message?: string;
            small?: string;
        };
        fontWeight?: {
            normal?: number;
            medium?: number;
            bold?: number;
        };
        lineHeight?: {
            tight?: string;
            normal?: string;
            relaxed?: string;
        };
    };

    // Spacing
    spacing?: {
        message?: string;
        bubble?: string;
        container?: string;
        compact?: string;
    };

    // Border radius
    borderRadius?: {
        bubble?: string;
        button?: string;
        window?: string;
        launcher?: string;
    };

    // Shadows
    boxShadow?: {
        launcher?: string;
        window?: string;
        message?: string;
    };

    // Dimensions
    dimensions?: {
        height?: string;
        width?: string;
        heightLarge?: string;
        widthLarge?: string;
        maxWidth?: string;
        minWidth?: string;
        padding?: string;
        mobile?: {
            position?: string;
            left?: number;
            right?: number;
            bottom?: number;
            top?: string;
            height?: string;
            width?: string;
            maxWidth?: string;
            minWidth?: string;
            borderRadius?: string;
            padding?: string;
        };
    };

    // Layout
    layout?: {
        display?: string;
        flexDirection?: string;
        zIndex?: number;
        zIndexLauncher?: number;
    };

    // Animations
    animations?: {
        enabled?: boolean;
        speed?: string;
        easing?: string;
    };

    // Legacy props
    launcherHoverShadow?: boolean;
    zIndex?: number;
    zIndexLauncher?: number;
    conversationWindowHeight?: string;
    conversationWindowWidth?: string;
    conversationWindowHeightLarge?: string;
    conversationWindowWidthLarge?: string;

    // Consent overlay
    consent?: {
        enabled?: boolean;
        text?: string | React.ReactNode;
        buttonText?: string;
        buttonColor?: string;
        privacyLinkUrl?: string;
        privacyLinkText?: string;
        storageKey?: string;
    };

    // Disclaimer text below input
    disclaimer?: {
        enabled?: boolean;
        text?: string;
        fontSize?: string;
    };

    // Custom CSS injection
    customCSS?: string;
    cssVariables?: Record<string, string>;
    componentClasses?: {
        launcher?: string;
        header?: string;
        messageUser?: string;
        messageBot?: string;
        input?: string;
    };
}

/**
 * A registered theme preset - a theme configuration with a mandatory name
 */
export type ThemePreset = ThemeConfig & { themeName: string };

/**
 * Merged theme context - combines preset + overrides
 */
export type MergedThemeContext = ThemeConfig & {
    preset?: ThemePreset;
};

const ThemeContext = React.createContext<MergedThemeContext>({
    themeName: undefined,
    showTitleBar: true,
    mainColor: '',
    conversationBackgroundColor: '',
    launcherBackgroundColor: '',
    launcherHoverShadow: true,
    userTextColor: '',
    userBackgroundColor: '',
    assistTextColor: '',
    assistBackgroundColor: '',
    zIndex: undefined,
    zIndexLauncher: undefined,
    conversationWindowHeight: undefined,
    conversationWindowWidth: undefined,
    conversationWindowHeightLarge: undefined,
    conversationWindowWidthLarge: undefined,
});

export default ThemeContext;
